import { createContext, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Accordion, Alert, Badge, BottomActionBar, Button, Carousel, ProgressBar, Skeleton } from '@dreamtree-org/twreact-ui';
import { CheckCircle2, XCircle, ShieldCheck, RotateCcw, BadgeCheck, ShoppingCart, Info, BatteryMedium, Scale, ClipboardCheck, BadgePercent, Award } from 'lucide-react';
import { KeyValue, Money, SectionCard } from '@/components';
import { formatDate } from '@/lib/format';
import { GRADE_LABEL, GRADE_VARIANT, batteryShown, conditionBadge, discountPct, gradeApplies, gradeLabel, specLine, specRows, specTable } from '../lib/listing';
import { attrsOf, typeKeyOf } from '../lib/shop';
import { viewRows } from '@/lib/productTypes';
import { follows } from '../lib/design';
import { useStorefront } from '../StorefrontContext';
import UnitImage from '../components/UnitImage';
import PinCheck from '../components/PinCheck';
import SellerCard from '../components/SellerCard';
import ListingCard from '../components/ListingCard';
import QtySelect from '../components/QtySelect';
import { isQtyItem, maxQtyOf } from '../lib/cart';
import { exchangeLabel, returnsLabel, typeWindowsText } from '../lib/terms';
import SizeChart from '../components/SizeChart';
import { conditionSummary } from '../lib/condition';
import ListingNotes from '../components/ListingNotes';
import { iconFor } from '../lib/typeIcons';
import { galleryIndexOf, hasOptions, optionsOf, unavailableText, valueState } from '../lib/variants';

/**
 * The product page's commerce slots (landing-kit PRODUCT_SLOTS). The page
 * (pages/ProductDetail.jsx) loads the unit, its inspection and the seller once
 * and shares them here; each slot draws its part in the owner's order.
 */
export const ProductCtx = createContext(null);
const useProduct = () => useContext(ProductCtx);

/**
 * Everything the product slots read: the unit, its inspection and seller
 * queries (`{ data, loading, error }`), the buy state (with the chosen quantity
 * for items sold by quantity, D60) and the template.
 * The Studio preview passes sample queries and no-op actions.
 */
export function buildProduct(sf, { l, tpl, insp, seller, busy = '', msg = null, setMsg = () => {}, add = () => {}, qty = 1, setQty = () => {}, variant = null, sel = {}, setSel = () => {} }) {
  // Real exact-unit photos only (MERCH-54): never placeholder shots standing in for the unit.
  // Illustrative images (MERCH-77, D91) come after them, each flagged and labelled "not this unit".
  const all = insp.data && Array.isArray(insp.data.gallery) ? insp.data.gallery : [];
  const real = all.filter((g) => !g.illustrative);
  const photos = real.length ? real : null;
  const illustrative = all.filter((g) => g.illustrative);
  const gallery = [...real, ...illustrative];
  const type = sf.shop.typeOf(l);
  const byQty = isQtyItem(l);
  const left = maxQtyOf(l);
  // A listing with options (MERCH-87): `l` is already the chosen variant's view (lib/variants asVariant).
  const inCart = sf.inCart(l.id, variant && variant.id != null ? variant.id : null);
  const blocked = unavailableText(l, sel);
  return {
    l,
    type,
    icon: (type || {}).icon,
    name: [l.brand, l.series, l.model_name].filter(Boolean).join(' '),
    off: discountPct(l),
    photos,
    illustrative,
    gallery,
    insp,
    seller,
    inCart,
    byQty,
    left,
    qty,
    setQty,
    soldOut: !inCart && ((byQty && left < 1) || Boolean(blocked)),
    variant,
    sel,
    setSel,
    blocked,
    busy,
    msg,
    setMsg,
    add,
    settings: tpl.settings,
    blocks: tpl.blocks,
    layout: layoutOf(tpl.template),
    paired: pairsGallery(tpl.blocks),
    similar: sf.listings.filter((x) => x.id !== l.id && typeKeyOf(x) === typeKeyOf(l)),
  };
}

/**
 * Page shape per product template: a two-column flow (half-width cards pair up)
 * or one centred column, and how the gallery and buy box share the first row.
 */
export const PRODUCT_LAYOUTS = {
  classic: { grid: 'grid gap-5 lg:grid-cols-2', full: 'lg:col-span-2', hero: 'grid gap-5 md:grid-cols-2 lg:grid-cols-12', gallery: 'lg:col-span-7', buy: 'lg:col-span-5' },
  immersive: { grid: 'grid gap-5 lg:grid-cols-2', full: 'lg:col-span-2', hero: 'grid gap-5 lg:grid-cols-12', gallery: 'lg:col-span-8', buy: 'lg:col-span-4 lg:sticky lg:top-36 lg:self-start' },
  compact: { grid: 'mx-auto grid max-w-2xl gap-5', full: '', hero: 'grid gap-5', gallery: '', buy: '' },
};
export const layoutOf = (template) => PRODUCT_LAYOUTS[template] || PRODUCT_LAYOUTS.classic;

/* ---------------- gallery ---------------- */
// Interim composition until twreact-ui ships ImageGallery (controlled index, zoom): https://github.com/dreamtree-org/dreamtree-org.twreact-ui/issues/182

function Thumbs({ x, view, setView, vertical }) {
  return (
    <div className={vertical ? 'hidden max-h-[32rem] w-20 shrink-0 flex-col gap-2 overflow-y-auto lg:flex' : '-mx-1 flex gap-2 overflow-x-auto px-1 pb-1'}>
      {x.gallery.map((g, i) => (
        <button
          key={g.url || g.caption}
          type="button"
          onClick={() => setView(i)}
          aria-label={`Show ${g.caption}`}
          aria-pressed={i === view}
          className={`w-16 shrink-0 rounded-lg sm:w-20 ${i === view ? 'ring-2 ring-primary-600 ring-offset-1' : 'opacity-80 hover:opacity-100'}`}
        >
          <UnitImage view={g.caption} src={g.url} size="sm" className="aspect-square" icon={x.icon} illustrative={g.illustrative} {...noticeOf(g)} />
        </button>
      ))}
    </div>
  );
}

function MainPhoto({ x, view }) {
  const g = x.gallery[view];
  return (
    <div className="relative min-w-0 flex-1">
      <UnitImage view={g.caption} src={g.url} size="lg" caption className="aspect-[4/3]" label={`${x.name}, ${g.caption}`} icon={x.icon} illustrative={g.illustrative} {...noticeOf(g)} />
      <span className="absolute bottom-2 right-2"><Badge size="xs" variant="secondary">{view + 1}/{x.gallery.length}</Badge></span>
    </div>
  );
}

/** A gallery entry's badge words: the server's label, short "Catalog" for a product model's image (MERCH-81). */
const noticeOf = (g) => (g.illustrative && g.label ? { notice: g.label, noticeShort: g.catalog ? 'Catalog' : 'Illustrative' } : {});

/** The caption line for the illustrative images at the end of the gallery (MERCH-77). */
export const galleryNote = (n, catalog = 0) => {
  const marks = [n > catalog ? '“Illustrative”' : null, catalog ? '“Catalog”' : null].filter(Boolean).join(' or ');
  return `The last ${n === 1 ? 'image is' : `${n} images are`} illustrative (marked ${marks}) and not of this unit.`;
};

/** Phones swipe through the photos; wider screens see them all at once. */
function MosaicView({ x }) {
  return (
    <>
      <div className="md:hidden">
        <Carousel showDots itemClassName="px-0.5">
          {x.gallery.map((g) => (
            <UnitImage key={g.url || g.caption} view={g.caption} src={g.url} size="lg" caption className="aspect-[4/3]" label={`${x.name}, ${g.caption}`} icon={x.icon} illustrative={g.illustrative} {...noticeOf(g)} />
          ))}
        </Carousel>
      </div>
      <div className="hidden grid-cols-2 gap-2 md:grid">
        {x.gallery.slice(0, 5).map((g, i) => (
          <div key={g.url || g.caption} className={i === 0 ? 'col-span-2' : ''}>
            <UnitImage view={g.caption} src={g.url} size={i === 0 ? 'lg' : 'md'} caption className={i === 0 ? 'aspect-[16/9]' : 'aspect-[4/3]'} label={`${x.name}, ${g.caption}`} icon={x.icon} illustrative={g.illustrative} {...noticeOf(g)} />
          </div>
        ))}
      </div>
    </>
  );
}

function ThumbsSideView({ x, view, setView }) {
  return (
    <>
      <div className="flex gap-3">
        <Thumbs x={x} view={view} setView={setView} vertical />
        <MainPhoto x={x} view={view} />
      </div>
      <div className="lg:hidden"><Thumbs x={x} view={view} setView={setView} /></div>
    </>
  );
}

function ThumbsBelowView({ x, view, setView }) {
  return (
    <>
      <MainPhoto x={x} view={view} />
      <Thumbs x={x} view={view} setView={setView} />
    </>
  );
}

/** Product setting `gallery` → its view (a registry, like PRODUCT_LAYOUTS). */
export const GALLERY_VIEWS = { mosaic: MosaicView, thumbs_side: ThumbsSideView, thumbs_below: ThumbsBelowView };

function PhotoGallery() {
  const x = useProduct();
  const [view, setView] = useState(0);
  // Choosing a variant shows its photo (MERCH-87), when it has one in this gallery.
  const variantMedia = x.variant ? x.variant.media_id : null;
  useEffect(() => {
    const i = galleryIndexOf(x.gallery, variantMedia);
    if (i >= 0) setView(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variantMedia]);
  const View = GALLERY_VIEWS[x.settings.gallery] || ThumbsBelowView;
  if (!x.gallery.length) {
    return (
      <section className="min-w-0" aria-label="Photos of this unit">
        <Alert variant="neutral">
          {x.byQty ? 'Photos of this product are pending.' : 'Photos of this exact unit are pending — see the inspection report below.'}
        </Alert>
      </section>
    );
  }
  return (
    <section className="min-w-0 space-y-3" aria-label="Photos of this unit">
      <View x={x} view={view} setView={setView} />
      <p className="text-xs opacity-70" data-testid="gallery-caption">
        {x.photos
          ? (x.byQty ? 'Photos of this product from the seller.' : `Photos of this exact unit (serial ${x.l.serial_masked || 'not listed'}), taken at inspection.`)
          : (x.byQty ? 'Photos of this product are pending.' : 'Photos of this exact unit are pending.')}
        {x.illustrative.length ? ` ${galleryNote(x.illustrative.length, x.illustrative.filter((g) => g.catalog).length)}` : null}
      </p>
    </section>
  );
}

/** The gallery slot also lays out the buy box beside it when the buy box comes straight after. */
function Gallery() {
  const x = useProduct();
  if (!x.paired) return <div className={x.layout.full}><PhotoGallery /></div>;
  const buy = x.blocks.find((b) => b.type === 'buy_box');
  return (
    <div className={`${x.layout.hero} ${x.layout.full}`}>
      <div className={`min-w-0 ${x.layout.gallery}`}><PhotoGallery /></div>
      <div className={`min-w-0 ${x.layout.buy}`}><BuyBoxBody props={(buy && buy.props) || {}} /></div>
    </div>
  );
}

/* ---------------- buy box ---------------- */

function specTiles(x, count) {
  const { l, type } = x;
  if (!count) return [];
  // The type's declared tiles (laptops: processor, RAM, storage, screen), else its first four card specs.
  const view = type ? viewRows(type, 'tiles', attrsOf(l)) : null;
  const tiles = !type ? []
    : view ? view.map((t) => ({ icon: iconFor(t.icon, Info), label: t.value }))
    : specRows(l, { ...type, attributes: type.attributes.filter((a) => a.card) }).slice(0, 4).map((r) => ({ icon: Info, label: `${r.value}` }));
  const extra = [
    { icon: BatteryMedium, label: batteryShown(l) ? `${l.battery_health_pct}% battery` : null },
    { icon: ShieldCheck, label: l.warranty_months ? `${l.warranty_months} mo warranty` : null },
  ];
  return [...tiles, ...extra].slice(0, count);
}

/**
 * Condition summary (MERCH-57): grade, battery, cosmetic notes, inspection, warranty
 * and returns at the top of the buy box, before the shopper scrolls to the passport.
 * Drawn inside the buy box because product block types are landing-kit's
 * PRODUCT_SLOTS; a standalone `condition_summary` block needs that declaration first.
 */
export function ConditionSummary() {
  const x = useProduct();
  return (
    <section className="rounded-lg border border-[var(--app-border)] bg-[var(--app-surface)] p-3" aria-label="Condition summary">
      <h2 className="mb-2 text-sm font-semibold">Condition summary</h2>
      <KeyValue items={conditionSummary(x.l, x.insp, x.seller).map(({ label, value }) => ({ label, value }))} />
    </section>
  );
}

/**
 * The variant picker (MERCH-87): colour swatches and size buttons, each value marked when the
 * combination it makes is out of stock or not offered. Drawn inside the buy box: a standalone
 * `variants` block needs landing-kit's PRODUCT_SLOTS to declare it first (see ConditionSummary).
 */
export function VariantPicker() {
  const x = useProduct();
  const { l } = x;
  if (!hasOptions(l)) return null;
  return (
    <section className="space-y-3" aria-label="Options">
      {optionsOf(l).map((o) => (
        <fieldset key={o.name} className="space-y-1.5">
          <legend className="text-sm"><span className="font-semibold">{o.name}:</span> {x.sel[o.name] || 'choose one'}</legend>
          <div className="flex flex-wrap gap-2">
            {o.values.map(({ value, swatch }) => {
              const state = valueState(l, x.sel, o.name, value);
              const on = x.sel[o.name] === value;
              const note = !state.offered ? 'not offered with this choice' : !state.available ? 'out of stock' : '';
              return (
                <Button
                  key={value}
                  size="sm"
                  variant={on ? 'primary' : 'outline'}
                  aria-pressed={on}
                  aria-label={note ? `${o.name} ${value}, ${note}` : `${o.name} ${value}`}
                  onClick={() => x.setSel(o.name, value)}
                  className={state.available ? '' : 'line-through opacity-60'}
                  data-testid={`option-${o.key || o.name}-${value}`}
                >
                  {/* A product colour the store set (#rrggbb, validated on save): data, not theme. */}
                  {o.style === 'swatch' && swatch ? <span className="mr-1.5 inline-block h-4 w-4 rounded-full border border-[var(--app-border)]" style={{ backgroundColor: swatch }} aria-hidden="true" /> : null}
                  {value}
                </Button>
              );
            })}
          </div>
        </fieldset>
      ))}
      {x.blocked ? <p className="text-sm text-error-600" role="status" data-testid="variant-blocked">{x.blocked}</p> : null}
    </section>
  );
}

function BuyBoxBody({ props }) {
  const x = useProduct();
  const sf = useStorefront();
  const { l } = x;
  const tiles = specTiles(x, Number(x.settings.spec_tiles) || 0);
  const comparing = sf.compareIds.includes(l.id);
  // From the seller feed (lib/store/policyTerms numbers), or the listing type's own window (MERCH-88).
  const returns = returnsLabel(x.seller.data, l);
  return (
    <section className="min-w-0 space-y-4" aria-label="Buy">
      <div className="flex flex-wrap gap-2">
        <span data-testid="buy-condition"><Badge variant="success">{conditionBadge(l)}</Badge></span>
        {gradeApplies(l) ? <Badge variant={GRADE_VARIANT[l.grade] || 'default'}>{GRADE_LABEL[l.grade] || gradeLabel(l.grade)}</Badge> : null}
      </div>
      <div>
        <h1 className="text-2xl font-bold leading-tight sm:text-3xl">{x.name}</h1>
        <p className="mt-1 text-sm opacity-80">{specLine(l, x.type)}</p>
        {l.sold_by ? <p className="mt-1 text-sm">Sold by <span className="font-semibold">{l.sold_by}</span> <span className="opacity-70">· an independent seller on this store</span></p> : null}
      </div>
      <ListingNotes l={l} />
      <div>
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <Money paise={l.price_paise} className="text-3xl font-bold" />
          {l.mrp_paise > l.price_paise ? <span className="text-sm line-through opacity-50">MRP <Money paise={l.mrp_paise} /></span> : null}
          {x.off ? <Badge variant="success" size="sm">{x.off}% off</Badge> : null}
        </div>
        <p className="text-xs opacity-70">Inclusive of GST · check delivery to your PIN code below</p>
      </div>
      <VariantPicker />
      {/* The brand's size chart for this type (MERCH-88): in the buy box until landing-kit has a size_chart slot. */}
      <SizeChart chart={l.size_chart} />
      <ConditionSummary />
      {tiles.length ? (
        <div className={`grid gap-2 ${tiles.length > 4 ? 'grid-cols-3' : 'grid-cols-2 sm:grid-cols-4 md:grid-cols-2 xl:grid-cols-4'}`}>
          {tiles.map(({ icon: Icon, label }, i) => (
            <div key={i} className="flex flex-col items-center gap-1 rounded-lg border border-[var(--app-border)] bg-[var(--app-surface)] p-2 text-center text-xs">
              <Icon className="h-5 w-5 text-primary-600" aria-hidden="true" />
              <span className={label ? '' : 'opacity-60'}>{label || 'Not specified'}</span>
            </div>
          ))}
        </div>
      ) : null}
      {x.byQty ? (
        <div className="flex items-end gap-3">
          {!x.inCart && x.left > 0 ? <div className="w-28"><QtySelect value={x.qty} max={x.left} onChange={x.setQty} /></div> : null}
          <p className={`pb-2 text-sm ${x.left > 0 ? 'text-success-600' : 'text-error-600'}`}>{x.left > 0 ? (x.left < 10 ? `Only ${x.left} left in stock` : 'In stock') : 'Out of stock'}</p>
        </div>
      ) : null}
      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" size="lg" fullWidth loading={x.busy === 'add'} disabled={x.soldOut} leftIcon={<ShoppingCart className="h-4 w-4" />} onClick={() => x.add(false)}>
          {x.inCart ? 'View cart' : 'Add to cart'}
        </Button>
        <Button size="lg" fullWidth loading={x.busy === 'buy'} disabled={x.soldOut} onClick={() => x.add(true)}>Buy now</Button>
      </div>
      {x.msg ? <Alert variant={x.msg.variant} dismissible onDismiss={() => x.setMsg(null)}>{x.msg.text}</Alert> : null}
      {props.show_compare !== false ? (
        <Button variant="ghost" fullWidth leftIcon={<Scale className="h-4 w-4" />} aria-pressed={comparing} onClick={() => {
            const err = sf.toggleCompare(l.id);
            x.setMsg(err ? { variant: 'warning', text: err } : null);
          }}>
          {comparing ? 'Added to compare' : 'Add to compare'}
        </Button>
      ) : null}
      {props.show_quote !== false ? (
        <Link to={`/quote?listing=${l.id}&title=${encodeURIComponent(l.title)}`} className="block min-h-[44px] text-center text-sm leading-[44px] text-primary-600 hover:underline">Buying several? Request a bulk quote</Link>
      ) : null}
      <ul className="grid grid-cols-3 gap-2 text-center text-xs">
        <li className="flex flex-col items-center gap-1"><RotateCcw className="h-5 w-5 text-primary-600" aria-hidden="true" />{returns}</li>
        <li className="flex flex-col items-center gap-1"><ShieldCheck className="h-5 w-5 text-primary-600" aria-hidden="true" />{l.warranty_months ? `${l.warranty_months} months warranty` : 'Warranty not specified'}</li>
        <li className="flex flex-col items-center gap-1"><BadgeCheck className="h-5 w-5 text-primary-600" aria-hidden="true" />Verified seller</li>
      </ul>
    </section>
  );
}

function BuyBox({ props }) {
  const x = useProduct();
  if (x.paired) return null; // drawn beside the gallery
  return <div className={x.layout.full}><BuyBoxBody props={props} /></div>;
}

/** Sticky buy bar on phones (product setting `sticky_buy_bar`); replaces the bottom nav there. */
export function StickyBuyBar() {
  const x = useProduct();
  return (
    <div className="md:hidden">
      <BottomActionBar align="between">
        <div className="min-w-0">
          <Money paise={x.l.price_paise} className="block text-lg font-bold leading-tight" />
          {x.off ? <span className="text-xs font-semibold text-success-700">{x.off}% off</span> : <span className="text-xs opacity-70">Inclusive of GST</span>}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" loading={x.busy === 'add'} disabled={x.soldOut} onClick={() => x.add(false)} aria-label={x.inCart ? 'View cart' : 'Add to cart'}>
            <ShoppingCart className="h-4 w-4" aria-hidden="true" />
          </Button>
          <Button loading={x.busy === 'buy'} disabled={x.soldOut} onClick={() => x.add(true)}>Buy now</Button>
        </div>
      </BottomActionBar>
      <BottomActionBar.Spacer />
    </div>
  );
}

/* ---------------- detail blocks ---------------- */

function Highlights({ props }) {
  const x = useProduct();
  const { l } = x;
  const r = x.insp.data;
  const items = [
    batteryShown(l) ? { icon: BatteryMedium, value: `${l.battery_health_pct}%`, label: 'Battery health' } : null,
    r ? { icon: ClipboardCheck, value: `${r.passed}/${r.total}`, label: 'Inspection checks passed' } : null,
    l.warranty_months ? { icon: ShieldCheck, value: `${l.warranty_months} mo`, label: 'Seller warranty' } : null,
    x.off ? { icon: BadgePercent, value: `${x.off}%`, label: 'Below MRP' } : null,
    gradeApplies(l) && l.grade ? { icon: Award, value: `Grade ${l.grade}`, label: 'Cosmetic condition' } : null,
  ].filter(Boolean).slice(0, 4);
  if (!items.length) return null;
  return (
    <section className={`space-y-3 ${x.layout.full}`} aria-label={props.title || 'Highlights'}>
      {props.title ? <h2 className="text-lg font-semibold">{props.title}</h2> : null}
      <ul className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {items.map(({ icon: Icon, value, label }) => (
          <li key={label} className="flex items-center gap-3 rounded-xl border border-[var(--app-border)] bg-[var(--app-surface)] p-4">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600"><Icon className="h-5 w-5" aria-hidden="true" /></span>
            <span className="min-w-0">
              <span className="block text-lg font-bold leading-tight">{value}</span>
              <span className="block text-xs opacity-70">{label}</span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Delivery() {
  return (
    <SectionCard title="Delivery">
      <PinCheck />
    </SectionCard>
  );
}

function Seller() {
  const x = useProduct();
  return <SellerCard query={x.seller} />;
}

/** Public passport results (lib/catalog/inspections publicPassport). Anything else renders as a fail. */
const RESULT_VIEW = {
  pass: { label: 'Pass', tone: 'text-success-700' },
  pass_with_note: { label: 'Pass, see note', tone: 'text-warning-700' },
};

function Inspection({ props }) {
  const x = useProduct();
  const q = x.insp;
  const r = q.data;
  return (
    <SectionCard
      className={x.layout.full}
      title={props.title || 'Digital Inspection Passport'}
      actions={r ? <Badge variant={r.failed ? 'warning' : 'success'}>{r.passed}/{r.total} checks passed</Badge> : null}
    >
      {q.error ? <Alert variant="error" title="Inspection report unavailable">{q.error}</Alert> : null}
      {!r && !q.error && q.loading ? <Skeleton count={6} /> : null}
      {!r && !q.error && !q.loading ? (
        <p className="text-sm">This unit has not been inspected yet. Ask the seller for the inspection report before buying.</p>
      ) : null}
      {r ? (
        <div className="space-y-3">
          <p className="text-xs opacity-70">
            Report {r.report_id} v{r.version} · Inspected {formatDate(r.inspected_at)} by {r.inspector}, {r.workshop}
          </p>
          <Accordion
            allowMultiple
            defaultOpenIndexes={props.expanded ? r.groups.map((_, i) => i) : [0]}
            size="sm"
            items={r.groups.map((g) => {
              const passed = g.items.filter((i) => i.result === 'pass' || i.result === 'pass_with_note').length;
              return {
                id: g.category,
                title: (
                  <span className="flex w-full flex-wrap items-center justify-between gap-x-3 pr-2">
                    <span className="font-medium">{g.category}</span>
                    <span className={`text-xs ${passed === g.items.length ? 'text-success-700' : 'text-warning-700'}`}>{passed}/{g.items.length} passed</span>
                  </span>
                ),
                content: (
                  <ul className="divide-y divide-[var(--app-border)] text-sm">
                    {g.items.map((i) => (
                      <li key={i.name} className="flex items-start gap-2 py-1.5">
                        {i.result === 'pass' ? (
                          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success-600" aria-label="Pass" />
                        ) : i.result === 'pass_with_note' ? (
                          <Info className="mt-0.5 h-4 w-4 shrink-0 text-warning-600" aria-label="Pass with note" />
                        ) : (
                          <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-error-600" aria-label="Fail" />
                        )}
                        <span className="min-w-0 flex-1">
                          {i.name}
                          {i.note ? <span className="block text-xs opacity-70">{i.note}</span> : null}
                        </span>
                        <span className={`text-xs font-semibold ${RESULT_VIEW[i.result] ? RESULT_VIEW[i.result].tone : 'text-error-700'}`}>{RESULT_VIEW[i.result] ? RESULT_VIEW[i.result].label : 'Fail'}</span>
                      </li>
                    ))}
                  </ul>
                ),
              };
            })}
          />
        </div>
      ) : null}
    </SectionCard>
  );
}

function Battery() {
  const x = useProduct();
  const q = x.insp;
  const b = q.data && q.data.battery;
  return (
    <SectionCard title="Battery health">
      {!b && q.loading ? (
        <Skeleton count={3} />
      ) : !b || b.health_pct == null ? (
        <p className="text-sm">Battery health was not measured for this unit. Ask the seller before buying.</p>
      ) : (
        <div className="space-y-3 text-sm">
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold">{b.health_pct}%</span>
            <span className="opacity-70">of original capacity</span>
          </div>
          <ProgressBar value={b.health_pct} max={100} size="md" variant="success" ariaLabel="Battery health" />
          <KeyValue
            items={[
              { label: 'Measured', value: formatDate(b.measured_at) },
              { label: 'Method', value: b.method },
              b.full_charge_mwh && b.design_mwh ? { label: 'Capacity', value: `${b.full_charge_mwh.toLocaleString('en-IN')} / ${b.design_mwh.toLocaleString('en-IN')} mWh` } : null,
              b.cycles != null ? { label: 'Charge cycles', value: b.cycles } : null,
              b.estimated_backup ? { label: 'Estimated backup', value: b.estimated_backup } : null,
            ]}
          />
        </div>
      )}
    </SectionCard>
  );
}

function Included() {
  const x = useProduct();
  const q = x.insp;
  const acc = (q.data && q.data.accessories) || (!q.loading ? [] : null);
  return (
    <SectionCard title="What's in the box">
      {!acc ? (
        <Skeleton count={3} />
      ) : (
        <ul className="space-y-2 text-sm">
          <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-success-600" aria-hidden="true" />The {x.type ? x.type.label.toLowerCase() : 'item'} (this exact unit)</li>
          {acc.map((a) => (
            <li key={a.name} className="flex items-start gap-2">
              {a.included ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success-600" aria-label="Included" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0 opacity-50" aria-label="Not included" />}
              <span className={a.included ? '' : 'opacity-60'}>
                {a.name} <span className="text-xs opacity-70">· {a.detail}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

function Specs({ props }) {
  const x = useProduct();
  const { l, type } = x;
  const table = specTable(l, type);
  return (
    <SectionCard title={props.title || 'Specifications'}>
      <KeyValue
        items={[
          { label: 'Brand', value: l.brand },
          { label: 'Series', value: l.series },
          { label: 'Model', value: l.model_name },
          ...(type ? [{ label: 'Type', value: type.label }] : []),
          ...table,
          { label: 'Condition', value: conditionBadge(l) },
          ...(gradeApplies(l) ? [{ label: 'Grade', value: GRADE_LABEL[l.grade] || l.grade }] : []),
          { label: 'Serial number', value: l.serial_masked ? `${l.serial_masked} (masked)` : null },
          { label: 'Listed on', value: formatDate(l.published_at) },
        ]}
      />
    </SectionCard>
  );
}

function Policies({ props }) {
  const x = useProduct();
  const { l } = x;
  const p = x.seller.data && x.seller.data.policies;
  return (
    <SectionCard title={props.title || 'Warranty and returns'}>
      {!p ? (
        <Skeleton count={4} />
      ) : (
        <div className="space-y-4 text-sm">
          <div>
            <div className="flex flex-wrap items-center gap-2 font-semibold">
              <ShieldCheck className="h-4 w-4 text-primary-600" aria-hidden="true" />
              {l.warranty_months ? `${l.warranty_months}-month seller warranty` : 'Warranty not specified'}
              {p.warranty ? <Badge size="xs" variant="outline">{p.warranty.version}</Badge> : null}
            </div>
            {p.warranty ? <p className="mt-1 opacity-80">{p.warranty.summary}</p> : null}
            {p.warranty && p.warranty.effective ? <p className="text-xs opacity-60">Policy effective {formatDate(p.warranty.effective)}</p> : null}
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2 font-semibold">
              <RotateCcw className="h-4 w-4 text-primary-600" aria-hidden="true" />
              {returnsLabel(x.seller.data, l)}
              {exchangeLabel(l) ? <span data-testid="exchange-window">· {exchangeLabel(l)}</span> : null}
              {p.returns.version ? <Badge size="xs" variant="outline">{p.returns.version}</Badge> : null}
            </div>
            {/* A type's own windows (MERCH-88) replace the store-wide summary's numbers. */}
            <p className="mt-1 opacity-80">{l.return_policy ? typeWindowsText(l.return_policy) : p.returns.summary}</p>
            {p.returns.effective ? <p className="text-xs opacity-60">Policy effective {formatDate(p.returns.effective)}</p> : null}
          </div>
        </div>
      )}
    </SectionCard>
  );
}

function Similar({ props }) {
  const x = useProduct();
  const items = x.similar.slice(0, Number(props.limit) || 4);
  if (!items.length) return null;
  return (
    <section className={`space-y-3 ${x.layout.full}`}>
      <h2 className="text-lg font-semibold">{props.title || `Similar ${x.type ? x.type.plural.toLowerCase() : 'items'}`}</h2>
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {items.map((l) => <ListingCard key={l.id} l={l} layout="stack" />)}
      </div>
    </section>
  );
}

/** Slot type → component (a registry: a new slot is one entry here plus its landing-kit declaration). */
export const PRODUCT_SLOT_COMPONENTS = {
  gallery: Gallery,
  buy_box: BuyBox,
  highlights: Highlights,
  delivery: Delivery,
  seller: Seller,
  inspection: Inspection,
  battery: Battery,
  included: Included,
  specs: Specs,
  policies: Policies,
  similar: Similar,
};

/** True when the gallery draws the buy box beside it (see Gallery). */
export const pairsGallery = (blocks) => follows(blocks, 'gallery', 'buy_box');
